import { useState, useEffect, useRef } from 'react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';

export default function MunicipalityAutocomplete({ id, value, onChange, error, required = false, className = '' }) {
    const [municipalities, setMunicipalities] = useState([]);
    const [filteredMunicipalities, setFilteredMunicipalities] = useState([]);
    const [inputValue, setInputValue] = useState(value || '');
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const wrapperRef = useRef(null);

    // Fetch municipalities from backend API proxy
    useEffect(() => {
        fetch('/api/ekatte/municipalities')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch municipalities');
                }
                return response.json();
            })
            .then(data => {
                console.log('Municipalities API response:', data);
                if (Array.isArray(data) && data.length > 0) {
                    // Extract municipality names - try different possible field names
                    const municipalityNames = data.map(item => {
                        return item.name || item.Name || item.municipality || item.Municipality || item.obshtina || '';
                    }).filter(name => name).sort();
                    
                    console.log('Extracted municipality names:', municipalityNames.length, 'municipalities');
                    setMunicipalities(municipalityNames);
                    setFilteredMunicipalities(municipalityNames);
                }
                setIsLoading(false);
            })
            .catch(error => {
                console.error('Error fetching municipalities:', error);
                setIsLoading(false);
            });
    }, []);

    // Update input value when prop changes
    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    // Handle input change and filter municipalities
    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        
        if (newValue.trim() === '') {
            setFilteredMunicipalities(municipalities);
        } else {
            const filtered = municipalities.filter(municipality =>
                municipality.toLowerCase().includes(newValue.toLowerCase())
            );
            setFilteredMunicipalities(filtered);
        }
        
        setIsOpen(true);
        setSelectedIndex(-1);
        onChange(newValue);
    };

    // Handle keyboard navigation
    const handleKeyDown = (e) => {
        if (!isOpen) return;

        const maxIndex = filteredMunicipalities.length - 1;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => (prev < maxIndex ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < filteredMunicipalities.length) {
                    handleSelect(filteredMunicipalities[selectedIndex]);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                break;
        }
    };

    // Handle municipality selection
    const handleSelect = (municipality) => {
        setInputValue(municipality);
        onChange(municipality);
        setIsOpen(false);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const placeholder = 'Изберете община...';

    return (
        <div ref={wrapperRef} className={`relative ${className}`}>
            <div className="relative">
                <input
                    id={id}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => setIsOpen(true)}
                    className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm ${
                        error ? 'border-red-300' : ''
                    }`}
                    placeholder={isLoading ? 'Зареждане...' : placeholder}
                    disabled={isLoading}
                    required={required}
                    autoComplete="off"
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
            </div>

            {isOpen && !isLoading && filteredMunicipalities.length > 0 && (
                <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    {filteredMunicipalities.map((municipality, index) => (
                        <div
                            key={index}
                            onClick={() => handleSelect(municipality)}
                            className={`relative cursor-pointer select-none py-2 px-3 ${
                                index === selectedIndex 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'hover:bg-indigo-600 hover:text-white'
                            }`}
                        >
                            {municipality}
                        </div>
                    ))}
                </div>
            )}

            {isOpen && !isLoading && filteredMunicipalities.length === 0 && inputValue && (
                <div className="absolute z-10 mt-1 w-full rounded-md bg-white py-2 px-3 text-base shadow-lg ring-1 ring-black ring-opacity-5 sm:text-sm">
                    <p className="text-gray-500">Няма намерени общини</p>
                </div>
            )}

            {error && (
                <p className="mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
}
